import type { AgentUseCase, DataField } from "./agentsService";

// ============================================================================
// AI-agent use-case templates. Picking a use case in the Agentes form pre-fills
// the objective / opening line / script / questions / data-to-collect with a
// sensible starting point (Dapta-style), which the user then tailors. Purely a
// frontend convenience — the backend just persists whatever is sent.
// ============================================================================

export interface AgentTemplate {
  useCase: AgentUseCase;
  label: string;
  icon: string;
  description: string;
  objective: string;
  openingLine: string;
  script: string;
  questions: string[];
  dataToCollect: DataField[];
  tone: string;
  guardrails: string;
  maxDurationMin: number;
}

const f = (key: string, label: string, required = false): DataField => ({ key, label, required });

export const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    useCase: "confirm_lead",
    label: "Confirmar lead",
    icon: "ri-checkbox-circle-line",
    description: "Llama a un prospecto recién registrado para confirmar y calificar sus datos.",
    objective:
      "Confirmar que los datos del prospecto son correctos, entender su necesidad y calificar el interés para que un asesor dé seguimiento.",
    openingLine:
      "Hola, le llamo de Coding Cloud por el registro que hizo en nuestro sitio. ¿Tiene un minuto para confirmar un par de datos?",
    script:
      "Preséntate brevemente, agradece su interés y confirma los datos que ya tenemos. Detecta qué problema quiere resolver y con qué urgencia. Cierra diciendo que un asesor le contactará por su medio preferido.",
    questions: [
      "¿Confirmo que su nombre y empresa son correctos?",
      "¿Cuál es el principal problema que busca resolver?",
      "¿Qué servicio o producto le interesa?",
      "¿Cómo se enteró de nosotros? (si fue un referido, ¿quién lo recomendó?)",
      "¿Qué tan pronto le gustaría avanzar?",
      "¿Cuál es su medio de contacto preferido?",
    ],
    dataToCollect: [
      f("nombre", "Nombre completo", true),
      f("empresa", "Empresa"),
      f("correo", "Correo electrónico"),
      f("necesidad", "Necesidad / problema principal", true),
      f("servicio_interes", "Servicio o producto de interés", true),
      f("como_nos_conocio", "Cómo nos conoció (fuente) / quién lo refirió", true),
      f("urgencia", "Urgencia / tiempo para avanzar"),
    ],
    tone: "cálido y profesional",
    guardrails:
      "No prometas precios ni condiciones. Si no puede hablar, ofrece llamar más tarde y cierra con cortesía.",
    maxDurationMin: 3,
  },
  {
    useCase: "outbound_sales",
    label: "Venta saliente",
    icon: "ri-megaphone-line",
    description: "Llama a una lista para ofrecer un producto o servicio y agendar interés.",
    objective:
      "Presentar el producto/servicio de forma breve y atractiva, detectar interés y agendar una reunión o pasar el contacto a un asesor.",
    openingLine:
      "Hola, le llamo de Coding Cloud. Estamos ayudando a empresas como la suya a [beneficio principal]. ¿Le interesaría conocer cómo en un par de minutos?",
    script:
      "Capta interés en los primeros segundos con el beneficio principal. Califica si es la persona adecuada y si tiene la necesidad. Si hay interés, agenda una reunión o confirma el mejor horario para que un asesor le contacte. Maneja objeciones con empatía, sin presionar.",
    questions: [
      "¿Es usted quien toma decisiones sobre este tema?",
      "¿Actualmente cómo resuelven [problema que ataca el producto]?",
      "¿Le interesaría una demostración o más información?",
      "¿Qué día y hora le viene mejor para que un asesor le contacte?",
    ],
    dataToCollect: [
      f("interesado", "¿Mostró interés? (sí/no)", true),
      f("persona_decision", "¿Es tomador de decisión?"),
      f("mejor_horario", "Mejor horario para seguimiento"),
      f("objecion", "Principal objeción o duda"),
    ],
    tone: "entusiasta pero respetuoso",
    guardrails:
      "No insistas si la persona dice que no le interesa. No inventes características ni precios que no te dieron. Respeta si pide no ser contactado.",
    maxDurationMin: 4,
  },
  {
    useCase: "product_intro",
    label: "Presentar producto",
    icon: "ri-rocket-2-line",
    description:
      "Llama en frío a una empresa para presentar un producto, despertar interés, conseguir un contacto y agendar una demo.",
    objective:
      "Presentar [PRODUCTO] de forma breve y atractiva, despertar interés, conseguir los datos de la persona indicada (nombre, cargo y medio de contacto) y, si hay interés, acordar día y hora para una demo guiada de 30 minutos sin costo.",
    openingLine:
      "Hola, le llamo de [EMPRESA]. Ayudamos a empresas como la suya a [beneficio principal] con [PRODUCTO]. ¿Me comunica con la persona que ve estos temas?",
    script:
      "1) Capta interés en los primeros segundos con el beneficio principal de [PRODUCTO]. 2) Confirma que hablas con quien decide o influye en el tema. 3) Detecta su reto actual y conecta 1-2 funciones de [PRODUCTO] que lo resuelven. 4) Si hay interés, propón una demo guiada de 30 min sin costo y acuerda día y hora. 5) Toma el nombre, cargo y el mejor medio de contacto de la persona. Maneja objeciones con empatía, sin presionar. El objetivo principal de la llamada es CONSEGUIR UN CONTACTO real.",
    questions: [
      "¿Es usted quien ve este tema en la empresa, o me podría comunicar con esa persona?",
      "¿Con quién tengo el gusto? ¿Cuál es su cargo?",
      "Hoy, ¿cómo resuelven [problema que ataca el producto]?",
      "¿Cuál es el principal reto que les gustaría resolver?",
      "¿Le gustaría una demo guiada de 30 minutos sin costo para verlo en acción?",
      "¿Qué día y horario le viene mejor para la demo, y a qué correo o WhatsApp le enviamos la invitación?",
    ],
    dataToCollect: [
      f("nombre_contacto", "Nombre de la persona de contacto", true),
      f("cargo", "Cargo / rol en la empresa"),
      f("reto_principal", "Principal reto a resolver"),
      f("interes_demo", "¿Mostró interés en la demo? (sí/no)", true),
      f("dia_hora_demo", "Día y hora preferidos para la demo"),
      f("correo", "Correo para enviar la invitación"),
      f("whatsapp", "WhatsApp / teléfono de contacto"),
      f("medio_contacto", "Medio de contacto preferido"),
    ],
    tone: "entusiasta, consultivo y respetuoso",
    guardrails:
      "No inventes funciones, integraciones ni precios que no aparezcan en el guion: si preguntan por precio, di que depende del plan y que el asesor lo detalla en la demo. No presiones si la persona no está interesada y respeta de inmediato si pide no ser contactada. Si no es la persona indicada, pide con cortesía con quién más podrías hablar o el mejor momento para volver a llamar.",
    maxDurationMin: 5,
  },
  {
    useCase: "debt_collection",
    label: "Cobranza",
    icon: "ri-money-dollar-circle-line",
    description: "Recuerda un pago pendiente y acuerda una fecha o medio de pago.",
    objective:
      "Recordar de forma respetuosa el pago pendiente, confirmar que la persona lo reconoce y acordar una fecha y medio de pago.",
    openingLine:
      "Hola, le llamo respecto a su cuenta con nosotros. Tenemos registrado un saldo pendiente y quería ver cómo podemos ayudarle a regularizarlo.",
    script:
      "Sé cordial y firme. Confirma que habla con la persona correcta antes de dar detalles. Recuerda el monto y la fecha de vencimiento. Pregunta el motivo del atraso, ofrece opciones (pago total, parcial o fecha de compromiso) y confirma el acuerdo. Cierra reiterando el compromiso acordado.",
    questions: [
      "¿Hablo con la persona titular de la cuenta?",
      "¿Reconoce el saldo pendiente?",
      "¿Cuándo podría realizar el pago?",
      "¿Por qué medio realizará el pago?",
    ],
    dataToCollect: [
      f("titular_confirmado", "¿Se confirmó al titular? (sí/no)", true),
      f("reconoce_deuda", "¿Reconoce la deuda? (sí/no)", true),
      f("fecha_compromiso", "Fecha de compromiso de pago"),
      f("medio_pago", "Medio de pago acordado"),
      f("motivo_atraso", "Motivo del atraso"),
    ],
    tone: "respetuoso y firme",
    guardrails:
      "No amenaces, no uses lenguaje agresivo ni reveles el motivo de la llamada a terceros. Cumple la normativa de cobranza: trato digno en todo momento. Si la persona no es el titular, no compartas detalles de la deuda.",
    maxDurationMin: 4,
  },
  {
    useCase: "custom",
    label: "Personalizado (en blanco)",
    icon: "ri-edit-line",
    description: "Empieza desde cero y define tú mismo cada campo.",
    objective: "",
    openingLine: "",
    script: "",
    questions: [],
    dataToCollect: [],
    tone: "",
    guardrails: "",
    maxDurationMin: 3,
  },
];

export const TEMPLATE_BY_USE_CASE: Record<AgentUseCase, AgentTemplate> = AGENT_TEMPLATES.reduce(
  (acc, t) => {
    acc[t.useCase] = t;
    return acc;
  },
  {} as Record<AgentUseCase, AgentTemplate>,
);
